import React, { useState } from 'react';
import { toast } from 'sonner';
import { X, Zap, CheckCircle } from 'lucide-react';
import { api } from '../../services/api';
import type { FollowupPriority } from '../../utils/followupPriority';
import { FollowupActionsForm } from './FollowupActionsForm';

interface PendingFollowup {
  id: string;
  pos_id: string;
  pos_name: string;
  pos_address: string;
  store_type: string;
  followup_materials: string[];
  followup_actions: string[];
  followup_notes?: string;
  followup_priority: string;
}

interface QuickVisitModalProps {
  isOpen: boolean;
  onClose: () => void;
  followup: PendingFollowup;
  onComplete: () => void;
}

const MATERIAL_LABELS: Record<string, string> = {
  pricetags: 'Pricetags',
  wobblers: 'Wobblers',
  posters: 'Posters',
  flyers: 'Flyers',
  merch: 'Merch',
};

const ACTION_LABELS: Record<string, string> = {
  meet_decision_maker: 'Meet Decision Maker',
  install_pos: 'Install POS Material',
  check_competitors: 'Check Competitor Activity',
  negotiate_placement: 'Negotiate Better Placement',
  photo_documentation: 'Photo Documentation',
};

export const QuickVisitModal: React.FC<QuickVisitModalProps> = ({
  isOpen,
  onClose,
  followup,
  onComplete,
}) => {
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Completed tasks tracking
  const [completedMaterials, setCompletedMaterials] = useState<string[]>([]);
  const [completedActions, setCompletedActions] = useState<string[]>([]);
  
  // New follow-up (optional)
  const [needsFollowup, setNeedsFollowup] = useState(false);
  const [followupMaterials, setFollowupMaterials] = useState<string[]>([]);
  const [followupActions, setFollowupActions] = useState<string[]>([]);
  const [followupNotes, setFollowupNotes] = useState('');
  const [followupPriority, setFollowupPriority] = useState<FollowupPriority>('medium');

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await api.post('/visits/quick', {
        pos_id: followup.pos_id,
        store_type: followup.store_type,
        notes: notes || `Quick visit: ${completedMaterials.length} materials delivered, ${completedActions.length} actions completed`,
        resolves_visit_id: followup.id,
        needs_followup: needsFollowup,
        followup_materials: needsFollowup ? followupMaterials : undefined,
        followup_actions: needsFollowup ? followupActions : undefined,
        followup_notes: needsFollowup ? followupNotes : undefined,
        followup_priority: needsFollowup ? followupPriority : undefined,
      });

      toast.success('Follow-up completed!');
      onComplete();
    } catch (error) {
      console.error('Failed to save quick visit:', error);
      toast.error('Failed to save visit');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-end sm:items-center justify-center sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto shadow-2xl overscroll-contain">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            <Zap className="text-green-600" size={28} />
            <div>
              <h2 className="text-xl font-black text-gray-900">Quick Visit</h2>
              <p className="text-sm text-gray-600">{followup.pos_name}</p>
            </div>
          </div>
          <button onPointerDown={onClose} className="p-2 hover:bg-gray-100 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center">
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Tasks to Complete */}
          <div className="space-y-4">
            {/* Materials Checklist */}
            {followup.followup_materials.length > 0 && (
              <div className="p-4 bg-purple-50 rounded-xl border-2 border-purple-200">
                <h3 className="font-bold text-purple-800 mb-3">Materials to Deliver</h3>
                <div className="space-y-2">
                  {followup.followup_materials.map((mat) => (
                    <label key={mat} className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={completedMaterials.includes(mat)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setCompletedMaterials([...completedMaterials, mat]);
                          } else {
                            setCompletedMaterials(completedMaterials.filter(m => m !== mat));
                          }
                        }}
                        className="w-5 h-5 rounded border-gray-300 text-purple-600"
                      />
                      <span className={completedMaterials.includes(mat) ? 'line-through text-gray-400' : 'text-gray-700'}>
                        {MATERIAL_LABELS[mat] || mat}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Actions Checklist */}
            {followup.followup_actions.length > 0 && (
              <div className="p-4 bg-blue-50 rounded-xl border-2 border-blue-200">
                <h3 className="font-bold text-blue-800 mb-3">Actions to Complete</h3>
                <div className="space-y-2">
                  {followup.followup_actions.map((action) => (
                    <label key={action} className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={completedActions.includes(action)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setCompletedActions([...completedActions, action]);
                          } else {
                            setCompletedActions(completedActions.filter(a => a !== action));
                          }
                        }}
                        className="w-5 h-5 rounded border-gray-300 text-blue-600"
                      />
                      <span className={completedActions.includes(action) ? 'line-through text-gray-400' : 'text-gray-700'}>
                        {ACTION_LABELS[action] || action}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Original Notes */}
            {followup.followup_notes && (
              <div className="p-4 bg-gray-50 rounded-xl border-2 border-gray-200">
                <h3 className="font-bold text-gray-700 mb-2">Original Notes</h3>
                <p className="text-sm text-gray-600">{followup.followup_notes}</p>
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block font-bold text-gray-900 mb-2">Visit Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full p-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
              rows={3}
              placeholder="Brief notes about this visit..."
            />
          </div>

          {/* New Follow-up */}
          <div className="border-t-2 border-gray-200 pt-6">
            <FollowupActionsForm
              needsFollowup={needsFollowup}
              onNeedsFollowupChange={setNeedsFollowup}
              materials={followupMaterials}
              onMaterialsChange={setFollowupMaterials}
              actions={followupActions}
              onActionsChange={setFollowupActions}
              notes={followupNotes}
              onNotesChange={setFollowupNotes}
              priority={followupPriority}
              onPriorityChange={setFollowupPriority}
            />
          </div>

          {/* Submit */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onPointerDown={onClose}
              className="flex-1 px-4 py-3.5 bg-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-300 active:scale-95 transition-all"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="button"
              onPointerDown={handleSubmit}
              className="flex-1 px-4 py-3.5 bg-green-600 text-white rounded-xl font-bold hover:bg-green-700 flex items-center justify-center gap-2 active:scale-95 transition-all"
              disabled={loading}
            >
              {loading ? 'Saving...' : (
                <>
                  <CheckCircle size={18} />
                  Complete Visit
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
